import React, { useState, useCallback, useEffect } from 'react';
import SensorWrap from './SensorWrap.styles';
import { DeleteSensorModal, SensorStatsContainer } from './SensorList.styles';
import { CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Spin } from 'antd';
import { Link } from 'react-router-dom';
import styled from "styled-components";
import moment from 'moment-timezone';
import Modal from '@iso/components/Feedback/Modal';
import IntlMessages from '@iso/components/utility/intlMessages';
import { sensorService } from '../../../../../services/sensor.service';
import { getSensorTypeLabel } from '../../../Sensors/doorSensorUtils';
import { deviceSensorService } from '../../../../../services/deviceSensor.service';
import AssetTrackingLocation from './AssetTrackingLocation';
import { 
    Temperature,
    Humidity,
    Pressure,
    Distance,
    AirQuality,
    Light,
    UV,
    Weather,
    Visibility,
    Electricity,
    Vibration,
    Relay,
    DefaultSensor
} from '../../../Sensors/sensorIcons';

const SensorList = (props) => {
    // Move all state declarations to the top
    const [visibleDeleteSensor, setVisibleDeleteSensor] = useState(false);
    const [sensorId, setSensorId] = useState(null);
    const [assetId, setAssetId] = useState(null);
    const [sensorStats, setSensorStats] = useState({});
    const [loadingSensors, setLoadingSensors] = useState({});

    // Get sensor type icon using custom sensor icons
    const getSensorTypeIcon = (sensorType, className = "sensor-icon") => {
        const iconMap = {
            temperature: Temperature,
            humidity: Humidity,
            moisture: Humidity,
            pressure: Pressure,
            distance: Distance,
            air_quality: AirQuality,
            light: Light,
            uv: UV,
            weather: Weather,
            visibility: Visibility,
            height: Distance,
            conductivity: Electricity,
            voltage: Electricity,
            vibration: Vibration,
            relay: Relay,
            latitude: Distance,
            longitude: Distance,
            default: DefaultSensor
        };
        
        const IconComponent = iconMap[sensorType.toLowerCase()] || iconMap.default;
        return <IconComponent className={className} />;
    };

    const onHandleDeleteSensor = (assetSensor) => {
        setVisibleDeleteSensor(true);
        setSensorId(assetSensor.sensor_id);
        setAssetId(assetSensor.asset_id);
    }

    const handleCancelSensor = () => {
        setVisibleDeleteSensor(false);
    } 

    const handleDeleteSensor = () => {
        sensorService.removeSensorFromAsset(sensorId, assetId).then(res => {
            if(res.code === '0000'){
                setVisibleDeleteSensor(false);
                props.getSensors();
            }
        });
    }

    // Fixed: Get sensor types from timeline data (same logic as SensorDetail)
    const getSensorTypes = (sensor) => {
        if (!sensor.timelines || !sensor.timelines.length) {
            return [];
        }

        const types = new Set();
        sensor.timelines.forEach(timeline => {
            Object.keys(timeline).forEach(key => {
                if (key !== 'time') {
                    types.add(key);
                }
            });
        });
        return Array.from(types);
    };

    // Fixed: Get last known value for any sensor type (same logic as SensorDetail)
    const getLastKnownValue = (sensor, sensorType) => {
        if (!sensor.timelines || !sensor.timelines.length || !sensorType) {
            return null;
        }

        for (let i = sensor.timelines.length - 1; i >= 0; i--) {
            const timeline = sensor.timelines[i];
            if (timeline[sensorType]) {
                return {
                    value: timeline[sensorType].value,
                    unit: timeline[sensorType].unit || '',
                    time: timeline[sensorType].time
                };
            }
        }
        return null;
    };

    // Fixed: Get statistics for a sensor type (same logic as SensorDetail)
    const getSensorTypeStats = (sensor, sensorType) => {
        if (!sensor.timelines || !sensorType) return null;

        const values = sensor.timelines
            .filter(timeline => timeline[sensorType])
            .map(timeline => parseFloat(timeline[sensorType].value) || 0);

        if (values.length === 0) return null;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

        return { min, max, avg: avg.toFixed(2) };
    };

    // Fixed: Updated fetchSensorData to match SensorDetail logic
    const fetchSensorData = useCallback(async (sensorId, deviceId, timezone = 'Australia/Melbourne') => {
        try {
            setLoadingSensors(prev => ({ ...prev, [sensorId]: true }));
            
            // Use last 24 hours like SensorDetail default
            const endDate = moment().tz(timezone);
            const startDate = moment().tz(timezone).subtract(23, 'hours').subtract(59, 'minutes');
            
            const params = { 
                start_date: startDate.format('DD/MM/YYYY HH:mm:ss'),
                end_date: endDate.format('DD/MM/YYYY HH:mm:ss'),
                sensor_id: sensorId,
                timezone: timezone
            };

            const response = await deviceSensorService.getDetail(deviceId, params);

            if (response && response.device) {
                let deviceData;
                if (Array.isArray(response.device)) {
                    deviceData = response.device.find(d => d.id === parseInt(deviceId)) || response.device[0];
                } else {
                    deviceData = response.device;
                }
                
                if (deviceData && deviceData.sensors && deviceData.sensors.length > 0) {
                    const targetSensor = deviceData.sensors.find(s => s.id === parseInt(sensorId));
                    
                    if (targetSensor) {
                        // Get all sensor types from the timeline data
                        const sensorTypes = getSensorTypes(targetSensor);
                        
                        // Get stats for each sensor type
                        const allStats = {};
                        const allCurrentValues = {};
                        
                        sensorTypes.forEach(sensorType => {
                            const stats = getSensorTypeStats(targetSensor, sensorType);
                            const currentValue = getLastKnownValue(targetSensor, sensorType);
                            
                            if (stats) {
                                allStats[sensorType] = stats;
                            }
                            if (currentValue) {
                                allCurrentValues[sensorType] = currentValue;
                            }
                        });
                        
                        setSensorStats(prev => ({
                            ...prev,
                            [sensorId]: {
                                stats: targetSensor.stats || {}, // Use API stats if available
                                calculatedStats: allStats, // Use calculated stats as fallback
                                currentValues: allCurrentValues,
                                sensorTypes: sensorTypes,
                                primarySensorType: sensorTypes[0] || null // Use first sensor type as primary
                            }
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching sensor data:', error);
        } finally {
            setLoadingSensors(prev => ({ ...prev, [sensorId]: false }));
        }
    }, []);

    const formatValue = (value, unit = '') => {
        if (value === null || value === undefined) return 'N/A';
        return `${value}${unit ? ` ${unit}` : ''}`;
    };

    // Fixed: Improved renderSensorStats to display all sensor types with icons
    const renderSensorStats = (sensorId) => {
        const stats = sensorStats[sensorId];
        const isLoading = loadingSensors[sensorId];

        if (isLoading) {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="small" />
                </div>
            );
        }

        if (!stats || !stats.sensorTypes || stats.sensorTypes.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    No sensor data available
                </div>
            );
        }

        const { currentValues, calculatedStats, sensorTypes } = stats;

        return (
            <SensorStatsContainer>
                {sensorTypes.map((sensorType, index) => {
                    const currentValue = currentValues[sensorType];
                    const sensorStatsData = calculatedStats[sensorType];
                    const unit = currentValue?.unit || '';

                    return (
                        <div key={sensorType} className="sensor-type-section">
                            {/* Sensor type header with icon */}
                            <div className="sensor-type-header">
                                {getSensorTypeIcon(sensorType, "sensor-type-icon")}
                                <span className="sensor-type-name">
                                    {getSensorTypeLabel(sensorType)}
                                </span>
                            </div>
                            
                            {/* Current value row */}
                            <div className="stats-row current-row">
                                <div className="stat-item current-item">
                                    <span className="stat-label">Current:</span>
                                    <span className="stat-value current">
                                        {currentValue ? formatValue(currentValue.value, unit) : 'N/A'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Min/Max/Avg row */}
                            {sensorStatsData && (
                                <div className="stats-row detail-row">
                                    <div className="stat-item">
                                        <span className="stat-label">Min:</span>
                                        <span className="stat-value">{formatValue(sensorStatsData.min, unit)}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Max:</span>
                                        <span className="stat-value">{formatValue(sensorStatsData.max, unit)}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Avg:</span>
                                        <span className="stat-value">{formatValue(sensorStatsData.avg, unit)}</span>
                                    </div>
                                </div>
                            )}

                            {/* Add separator between sensor types (except for last one) */}
                            {index < sensorTypes.length - 1 && <div className="sensor-separator" />}
                        </div>
                    );
                })}
            </SensorStatsContainer>
        );
    };

    // Fetch sensor data for non-asset tracking sensors
    useEffect(() => {
        if (props.data && props.data.length > 0) {
            props.data.forEach(assetSensor => {
                if (assetSensor.sensor.part_number !== "ASSET_TRACKING") {
                    fetchSensorData(assetSensor.sensor.id, assetSensor.sensor.device_id);
                }
            });
        }
    }, [props.data, fetchSensorData]);

    return (
        <SensorWrap>
            {
                props.data.map((assetSensor) => {
                    return (
                        <div className="wrap-card-sensor" key={assetSensor.sensor_id}>
                            <div className="wrap-btn-sensor">
                                <Button 
                                    type="link" 
                                    className="btn-n-br btn-view-sensor" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        props.onSensorClick(assetSensor.sensor);
                                    }}
                                    title="View sensor details"
                                >
                                    <EyeOutlined />
                                </Button>

                                <Button
                                    type="link"
                                    className="btn-n-br btn-delete-sensor"
                                    onClick={onHandleDeleteSensor.bind(this, assetSensor)}
                                >
                                    <CloseOutlined/>
                                </Button>
                            </div>

                            {/* Show alternate card if we have assetSensor AND part_number is "ASSET_TRACKING" */}
                            {(assetSensor && assetSensor.sensor.part_number === "ASSET_TRACKING") && (
                                <AssetTrackingLocation 
                                    deviceId={assetSensor.sensor.device_id} 
                                    sensorId={assetSensor.sensor.id}
                                    timezone="Australia/Melbourne" 
                                />
                            )}

                            {/* Show regular card with stats if we have assetSensor but part_number is NOT "ASSET_TRACKING" */}
                            {(assetSensor && assetSensor.sensor.part_number !== "ASSET_TRACKING") && (
                                <Card 
                                    title={assetSensor.sensor.name} 
                                    bordered={true} 
                                    className="card-sensor-title"
                                >                                
                                    <div>
                                        <p><b>Device:</b> {assetSensor.sensor.device?.terminal_id}</p>
                                        <p><b>Vendor:</b> {assetSensor.sensor.device?.supplier}</p>
                                        <p><b>Created Date:</b> {assetSensor.created_at}</p>
                                    </div>
                                    
                                    {/* Add sensor statistics */}
                                    {renderSensorStats(assetSensor.sensor.id)}
                                </Card>
                            )}
                        </div>
                    )
                })
            }

            <DeleteSensorModal 
                className="modal-delete-sensor" 
                title={<IntlMessages id="propertyPage.mobModal.sensorTab.deleteButton" />}
                visible={visibleDeleteSensor} 
                onOk={handleDeleteSensor} 
                onCancel={handleCancelSensor}>
                <p>{<IntlMessages id="propertyPage.mobModal.sensorTab.confirmDeleteText" />}</p>
            </DeleteSensorModal>
        </SensorWrap>
    )
}

export default SensorList;